import { useRef, useState } from "react";
import { Markdown } from "zudoku/components";
import { SyntaxHighlight } from "zudoku/ui/SyntaxHighlight";

export type RecipeStep = {
  title: string;
  body?: string;
  /**
   * A single snippet, or one snippet per language keyed by language id. Keys
   * must match the ids given in the `language` prop.
   */
  code?: string | Record<string, string>;
};

/** Display names for language ids that plain capitalization gets wrong. */
const LANGUAGE_LABELS: Record<string, string> = {
  javascript: "JavaScript",
  typescript: "TypeScript",
  csharp: "C#",
  php: "PHP",
};

const languageLabel = (id: string) =>
  LANGUAGE_LABELS[id] ?? id.charAt(0).toUpperCase() + id.slice(1);

/**
 * Interactive recipe layout: step cards on the left, a sticky code panel on the
 * right. Selecting a step highlights its code fragment; the fragments together
 * form the full runnable script.
 *
 * Pass several ids to `language` to offer the script in more than one language.
 * Each step then supplies its snippet per language, and the panel header
 * renders a switcher. Zudoku's own CodeTabs is not used here because it renders
 * one block per tab, which cannot hold the per-step fragments this layout
 * highlights.
 *
 * Registered as a global MDX component in zudoku.config.tsx, so recipe pages
 * use <Recipe steps={[...]} /> without importing it.
 */
export const Recipe = ({
  steps,
  language = "python",
  response,
}: {
  steps: RecipeStep[];
  language?: string | string[];
  response?: string;
}) => {
  const languages = Array.isArray(language) ? language : [language];
  const [selected, setSelected] = useState(languages[0] ?? "python");
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const fragmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const codeFor = (step: RecipeStep) =>
    typeof step.code === "string" ? step.code : step.code?.[selected];

  const script = steps.map(codeFor).filter(Boolean).join("\n\n");

  const select = (index: number) => {
    setActive(index);
    fragmentRefs.current[index]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  };

  const copyScript = async () => {
    await navigator.clipboard.writeText(script);
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="not-prose grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.45fr)] gap-4 my-6">
      <div className="flex flex-col gap-2">
        {steps.map((step, index) => (
          <button
            key={step.title}
            type="button"
            onClick={() => select(index)}
            className={`text-left rounded-lg border p-4 transition-colors ${
              active === index
                ? "border-primary bg-accent"
                : "border-border bg-card hover:bg-muted"
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  active === index
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="font-medium text-sm">{step.title}</div>
                {step.body && (
                  <div className="text-sm text-muted-foreground mt-1 [&_code]:font-mono [&_code]:text-xs [&_p]:m-0">
                    <Markdown content={step.body} />
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="lg:sticky lg:top-24 self-start w-full">
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border bg-muted">
            {languages.length > 1 ? (
              <div className="flex items-center gap-1">
                {languages.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelected(id)}
                    className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                      selected === id
                        ? "bg-card text-foreground"
                        : "text-muted-foreground hover:text-accent-foreground"
                    }`}
                  >
                    {languageLabel(id)}
                  </button>
                ))}
              </div>
            ) : (
              <span className="text-xs font-medium text-muted-foreground">
                {languageLabel(selected)}
              </span>
            )}
            <button
              type="button"
              onClick={copyScript}
              className="shrink-0 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="recipe-code max-h-[32rem] overflow-y-auto bg-card">
            {steps.map((step, index) => {
              const code = codeFor(step);
              if (!code) return null;

              return (
                <div
                  key={step.title}
                  ref={(element) => {
                    fragmentRefs.current[index] = element;
                  }}
                  onClick={() => setActive(index)}
                  className={`cursor-pointer border-l-2 transition-all ${
                    active === index
                      ? "border-l-primary opacity-100 bg-accent/40"
                      : "border-l-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <SyntaxHighlight
                    embedded
                    showLineNumbers
                    showLanguageIndicator={false}
                    language={selected}
                    code={code}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {response && (
          <div className="rounded-lg border border-border overflow-hidden mt-3">
            <div className="px-4 py-2 border-b border-border bg-muted">
              <span className="text-xs font-medium text-muted-foreground">
                Output
              </span>
            </div>
            <div className="recipe-code max-h-60 overflow-y-auto bg-card">
              <SyntaxHighlight
                embedded
                showLineNumbers
                showLanguageIndicator={false}
                language="json"
                code={response}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
